import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useAtom, useAtomValue } from 'jotai';

import { UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { Pending } from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import { SelectSettings } from '~teacher/launchv2/steps/SelectSettings';
import { useBeginQuiz } from '~teacher/launchv2/useBeginQuiz';

import {
  isActivityStartingAtom,
  isLaunchWorkspaceQuizDialogOpenAtom,
  quizNameAtom,
} from './atoms';
import {
  LaunchContextProvider,
  LaunchRoute,
} from './context/LaunchModalContext';
import { RoomProvider } from './context/RoomContext';

interface Props {
  initialRoomName: string;
  initialRoomId: number;
  isRoomRostered: boolean;
}

export function LaunchWorkspaceQuizDialog({
  initialRoomId,
  initialRoomName,
  isRoomRostered,
}: Props) {
  const [isOpen, setIsOpen] = useAtom(isLaunchWorkspaceQuizDialogOpenAtom);

  const handleClose = () => {
    setIsOpen({ type: 'close' });
  };

  return (
    <LaunchContextProvider launchRoute={LaunchRoute.WorkspaceQuizList}>
      <RoomProvider
        roomId={initialRoomId}
        roomName={initialRoomName}
        isRoomRostered={isRoomRostered}
      >
        <DialogComponent isOpen={isOpen} onClose={handleClose} />
      </RoomProvider>
    </LaunchContextProvider>
  );
}

interface DialogComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

function DialogComponent({ isOpen, onClose }: DialogComponentProps) {
  const isActivityStarting = useAtomValue(isActivityStartingAtom);
  const quizName = useAtomValue(quizNameAtom);

  return (
    <Dialog
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={onClose}
      id={'launch-quiz-dialog'}
      title={
        <DialogTitle
          isActivityStarting={isActivityStarting}
          quizName={quizName}
        />
      }
      size='large'
    >
      {/* dialog doesnt unmount, so move to separate component to unmount properly */}
      {isOpen ? (
        <>
          <div className="pt-2">
            <Divider />
          </div>
          <Content />
        </>
      ) : null}
    </Dialog>
  );
}

interface DialogTitleProps {
  quizName: string;
  isActivityStarting: boolean;
}

function DialogTitle({ isActivityStarting, quizName }: DialogTitleProps) {
  if (isActivityStarting) {
    return null;
  }

  return (
    <Container row verticalCenter style={{ height: '30px' }}>
      <span
        className={
          'ml-3 font-display text-display leading-8 text-left truncate font-700 text-blue-900 hover:no-underline'
        }
      >
        {quizName ? quizName : null}
      </span>
    </Container>
  );
}

function Content() {
  const isActivityStarting = useAtomValue(isActivityStartingAtom);
  const { beginQuiz } = useBeginQuiz();

  // loading state
  if (isActivityStarting) {
    return (
      <div className={'m-auto flex flex-col items-center justify-center'}>
        <Pending />
        <h4 className={'text-md leading-8 text-blue-900 font-display font-400'}>
          {translate('Starting Activity…')}
        </h4>
      </div>
    );
  }

  return (
    <>
      <Container className={'pt-2'}>
        <Divider />
      </Container>
      {/*NOTE: Changing this height will affect Showbie Integration view as well*/}
      <Container style={{ height: '575px' }}>
        <SelectSettings />
        <Divider />
        <Container className={'items-end pt-4'}>
          <UIButton
            appearance={'primary'}
            isDisabled={isActivityStarting}
            onClick={beginQuiz}
          >
            {translate('Launch')}
          </UIButton>
        </Container>
      </Container>
    </>
  );
}
